// Lib
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from '../../../../../node_module_clones/recompose';
import { compact, isEmpty } from 'lodash/fp';

// Utils
import measurementsConnect from '../../../../components/measurementsStore/measurementsConnect';
import { createShallowSelector } from '../../../../utils/milanoteReselect/milanoteReselect';
import {
    getNotificationElementId,
    getNotificationMentionElementId,
    getNotificationType,
    getNotificationViewedTimestamp,
} from '../../../../../common/notifications/notificationModelUtils';
import {
    getUnseenCommentIds,
    getUnseenElementChangesIds,
} from '../../../../../common/notifications/notificationsListUtils';
import { getCurrentBoardViewedTime } from '../../../../element/selectors/currentBoardSelector';

// Selectors
import cleansedNotificationsSelector from '../../../../notifications/cleansedNotificationsSelector';
import { getCurrentBoardId } from '../../../../reducers/currentBoardId/currentBoardIdSelector';
import {
    getPoiCommentId,
    getPoiElementId,
    getPoiSeen,
    getPoiMap,
} from '../../../../components/pointsOfInterest/poiSelector';

// Actions
import { markNotificationsAsSeen } from '../../../../notifications/notificationActions';

// Constants
import { NOTIFICATION_TYPES } from '../../../../../common/notifications/notificationConstants';

const seenPointsOfInterests = createShallowSelector(getPoiMap, (pois) => pois.filter(getPoiSeen));

const seenPoiElementAndCommentIds = createShallowSelector(seenPointsOfInterests, (seenPois) =>
    seenPois.reduce((acc, poi) => {
        const id = getPoiElementId(poi) || getPoiCommentId(poi);

        if (id) {
            acc[id] = true;
        }

        return acc;
    }, {}),
);

const mapMeasurementsStateToProps = createStructuredSelector({
    seenPoiIds: seenPoiElementAndCommentIds,
});

const currentBoardUnseenNotificationsSelector = createShallowSelector(
    getCurrentBoardId,
    cleansedNotificationsSelector,
    (currentBoardId, notifications) =>
        notifications.filter(
            (notification) =>
                !getNotificationViewedTimestamp(notification) &&
                getNotificationElementId(notification) === currentBoardId,
        ),
);

// TODO Seen POIs selector (from measurements connect)

const isImmediatelySeenNotification = (notification) => {
    const notificationType = getNotificationType(notification);

    switch (notificationType) {
        case NOTIFICATION_TYPES.BOARD_VIEW:
        case NOTIFICATION_TYPES.NEW_TEMPLATE:
        case NOTIFICATION_TYPES.SHARE:
        case NOTIFICATION_TYPES.UNSHARE:
            return true;
        default:
            return false;
    }
};

const notificationIsNowSeen = (notification, lastBoardView, seenPoiIds) => {
    if (isImmediatelySeenNotification(notification)) return true;

    // Otherwise, ensure all the points of interest relating to this notification have been seen
    const ids = compact([
        ...getUnseenCommentIds(notification),
        ...getUnseenElementChangesIds(notification, lastBoardView),
        getNotificationMentionElementId(notification),
    ]);

    if (isEmpty(ids)) return true;

    // Otherwise compare to seenPoiIds
    return ids.every((id) => seenPoiIds[id]);
};

const currentBoardNowSeenNotificationsSelector = createShallowSelector(
    currentBoardUnseenNotificationsSelector,
    getCurrentBoardViewedTime,
    (state, { seenPoiIds }) => seenPoiIds,
    (notifications, lastBoardView, seenPoiIds) =>
        notifications
            .filter((notification) => notificationIsNowSeen(notification, lastBoardView, seenPoiIds))
            .keySeq()
            .toArray(),
);

const mapStateToProps = createStructuredSelector({
    nowSeenNotificationIds: currentBoardNowSeenNotificationsSelector,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchMarkNotificationsAsSeen: (notificationIds) => dispatch(markNotificationsAsSeen(notificationIds)),
});

export default compose(measurementsConnect(mapMeasurementsStateToProps), connect(mapStateToProps, mapDispatchToProps));
